import { useState, useMemo, useCallback } from 'react';
import { RoadmapProject, RoadmapFilters } from '../types';
import {
  applyRoadmapFilters,
  getUniquePrograms,
  getUniqueStatuses,
  getUniqueL7Managers,
  getUniqueThemes,
  getUniqueAssignees,
  getUniqueOpDecisions,
  getUniqueMissionFits,
  getUniqueTargetYears,
  getUniqueRequestTypes,
} from '../utils/roadmapUtils';

interface UseRoadmapFiltersProps {
  projects: RoadmapProject[];
}

export const useRoadmapFilters = ({ projects }: UseRoadmapFiltersProps) => {
  const [filters, setFilters] = useState<RoadmapFilters>({
    programs: [],
    statuses: [],
    l7Managers: [],
    themes: [],
    assignees: [],
    opDecisions: [],
    missionFits: [],
    targetYears: [],
    requestTypes: [],
    searchQuery: ''
  });

  // Get unique values for filter dropdowns
  const filterOptions = useMemo(() => ({
    programs: getUniquePrograms(projects),
    statuses: getUniqueStatuses(projects),
    l7Managers: getUniqueL7Managers(projects),
    themes: getUniqueThemes(projects),
    assignees: getUniqueAssignees(projects),
    opDecisions: getUniqueOpDecisions(projects),
    missionFits: getUniqueMissionFits(projects),
    targetYears: getUniqueTargetYears(projects),
    requestTypes: getUniqueRequestTypes(projects),
  }), [projects]);

  // Apply filters to projects
  const filteredProjects = useMemo(() => 
    applyRoadmapFilters(projects, filters),
    [projects, filters]
  );

  // Update a specific filter
  const updateFilter = useCallback((filterKey: keyof RoadmapFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  }, []);

  // Toggle a value in a multi-select filter
  const toggleFilterValue = useCallback((filterKey: 'programs' | 'statuses' | 'l7Managers', value: string) => {
    setFilters(prev => {
      const currentValues = prev[filterKey];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      
      return {
        ...prev,
        [filterKey]: newValues
      };
    });
  }, []);

  // Clear a specific filter
  const clearFilter = useCallback((filterKey: keyof RoadmapFilters) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: Array.isArray(prev[filterKey]) ? [] : ''
    }));
  }, []);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setFilters({
      programs: [],
      statuses: [],
      l7Managers: [],
      themes: [],
      assignees: [],
      opDecisions: [],
      missionFits: [],
      targetYears: [],
      requestTypes: [],
      searchQuery: ''
    });
  }, []);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return filters.programs.length > 0 ||
           filters.statuses.length > 0 ||
           filters.l7Managers.length > 0 ||
           filters.themes.length > 0 ||
           filters.assignees.length > 0 ||
           filters.opDecisions.length > 0 ||
           filters.missionFits.length > 0 ||
           filters.targetYears.length > 0 ||
           filters.requestTypes.length > 0 ||
           filters.searchQuery.length > 0;
  }, [filters]);

  return {
    filters,
    filterOptions,
    filteredProjects,
    updateFilter,
    toggleFilterValue,
    clearFilter,
    clearAllFilters,
    hasActiveFilters
  };
};